import { Table, Tooltip, Typography } from "antd";
import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import ACTIONS from "../store/action";
import { useLocation } from "react-router";

import { ApiNames } from "../api/api";
import AttachIcon from "../assets/svg/AttachIcon";
import FreeIcon from "../assets/svg/FreeIcon";
import NoOrdersFoundIcon from "../assets/svg/NoOrdersFoundIcon";
import NoteIcon from "../assets/svg/NoteIcon";
import ReplacementIcon from "../assets/svg/ReplacementIcon";
import { WHITE } from "../constant/colors";
import { PAGE_SIZE, ViewType } from "../constant/general";
import { SORT_DIRECTIONS, getColumnSortOrder, getSortingChanges } from "../constant/sort";
import { fontWeights } from "../constant/styles";
import {
  selectIsFetching,
  selectPatientTableColumns,
  selectSite,
  selectSorting,
  selectViewType
} from "../store/selector";
import { formatAssigneesToDisplay } from "../utils/formaters";

import { DATE_FORMAT_2 } from "../utils/date";
import { scrollTableToTop } from "../utils/misc";
import { defaultSort } from "../utils/sort";
import { ActionButton } from "./ActionButton";
import { PatientNameColumn, withSorting } from "./customComponent/CommonColumns";
import LinkableCell from "./LinkableCell";
import { FilterListForm } from "./SavedList/FilterListForm";
import { TableStyle } from "./SavedList/TableStyle";
import { StatusIcon } from "./StatusIcon";
import { Routes } from "../constant/routes";
import { STORAGE_KEYS } from "../constant/storageKeys";
import { getDoseFormCategory } from "../utils/drugs";

const { Text } = Typography;
const INVALID_DATE = "-";
const tableId = "main-list-view";

const StyledTable = styled(Table)`
  ${TableStyle}
  .ant-table-row {
    cursor: pointer;
  }
  .ant-table {
    margin-left: ${(props) => (props.filtersVisible ? `8px` : props.isSearch ? `50px` : `0px`)};
    max-height: ${(props) =>
      props.isSavedListView ? `calc(100vh - 340px)` : props.isSearch ? `calc(100vh - 280px)` : `calc(100vh - 310px)`};
  }
  .ant-pagination {
    left: ${(props) => (props.filtersVisible ? `8px` : `0px`)};
  }
  .ant-pagination-total-text {
    margin-left: ${(props) => (props.filtersVisible || props.isSavedListView ? `5px` : `50px`)};
  }
  .ant-table-empty {
    max-height: ${(props) => (props.isSearch ? `calc(100vh - 250px)` : `calc(100vh - 300px)`)};
    .ant-table-tbody {
      height: ${(props) => (props.isSearch ? `calc(100vh - 310px)` : `calc(100vh - 355px)`)};
    }
  }
`;

const renderWithLink = (record, renderFn, searchBy, searchText) => {
  const basePath = new URL(window?.location?.href).pathname;
  const target = {
    pathname: basePath,
    search: `?patient=${record?.patient?.id}&pharmacyPatient=${record?.id}${searchBy ? `&searchBy=${searchBy}` : ""}`
  };

  // Preserve searchText in state when navigating within search-results to prevent redirect to recent patients
  if (basePath.includes(Routes.SEARCH_RESULTS) && searchText) {
    target.state = { searchText };
  }

  return <LinkableCell to={target}>{renderFn()}</LinkableCell>;
};

function OrdersTableComponent({
  t,
  patients,
  startSearch,
  total,
  onClickTable,
  onHeaderCell,
  tableColumns,
  isSearch,
  fetchOrders,
  currentPage,
  isFetching,
  isLoadingEnabled,
  isFetchingSavedList,
  isSavedListView,
  filtersVisible
}) {
  const dispatch = useDispatch();
  const { search, state } = useLocation();
  const searchParams = new URLSearchParams(search);

  const searchBy = searchParams.get("searchBy") || "";
  const basePath = new URL(window?.location?.href).pathname;
  // On search-results page: prioritize state (even if empty), fallback to localStorage for new tabs
  // On other pages: only use state
  const searchText = basePath.includes(Routes.SEARCH_RESULTS)
    ? (state && "searchText" in state
        ? state.searchText
        : localStorage.getItem(STORAGE_KEYS.SEARCH_RESULTS_CURRENT_TEXT)) || ""
    : state?.searchText || "";

  const setCurrentPageAct = (page) => dispatch(ACTIONS.setCurrentPage(page));
  const setSearchResultCurrentPageAct = (page) => dispatch(ACTIONS.setSearchResultCurrentPage(page));
  const setSortingAct = (sorting) => dispatch(ACTIONS.setSorting(sorting));
  const sorting = useSelector(selectSorting);
  const isLoading = useSelector(selectIsFetching([ApiNames.SearchWithoutRefresh]));
  const isLoadingSearchWithText = useSelector(selectIsFetching([ApiNames.SearchByTextAndFilters]));
  const isLoadingSearchFreeText = useSelector(selectIsFetching([ApiNames.SearchFreeText]));
  const setIsLoadingEnabledAct = (isLoadingEnabled) => dispatch(ACTIONS.setIsLoadingEnabled(isLoadingEnabled));
  const setFormPopupsAct = (visible) => dispatch(ACTIONS.setFormPopups(visible));
  const viewType = useSelector(selectViewType);
  const orderTableLoading =
    ((!startSearch && isSearch) ||
      isLoading ||
      isLoadingSearchWithText ||
      isLoadingSearchFreeText ||
      (viewType === ViewType.SavedList.key ? isFetchingSavedList : false)) &&
    isLoadingEnabled;

  const columns = [
    withSorting(tableColumns.name_id, {
      key: tableColumns.name_id.key,
      dataIndex: tableColumns.name_id.key,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: getColumnSortOrder(sorting, "name_id"),
      width: 200,
      render: (text, record) =>
        renderWithLink(record, () => <PatientNameColumn record={record.patient} />, searchBy, searchText),
      ...onClickTable()
    }),
    {
      ...tableColumns.dob,
      dataIndex: tableColumns.dob.key,
      key: tableColumns.dob.key,
      width: 100,
      render: (text, record) => (record.patient?.dob ? moment(record.patient.dob).format("MM/DD/YYYY") : ""),
      ...onClickTable()
    },
    {
      ...tableColumns.isActive,
      dataIndex: tableColumns.isActive.key,
      key: tableColumns.isActive.key,
      width: 100,
      render: (text, record) => (record.isActive ? t("header_searchbar.active") : t("header_searchbar.in_active")),
      ...onClickTable()
    },
    {
      ...tableColumns.assigneeNames,
      title: () => {
        return <div>{tableColumns.assigneeNames.title}</div>;
      },
      dataIndex: tableColumns.assigneeNames.key,
      key: tableColumns.assigneeNames.key,
      width: 150,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div
              style={{
                display: "flex",
                minWidth: "100px"
              }}
            >
              <Tooltip title={<Text>{text}</Text>} color={WHITE}>
                <Text ellipsis={true} style={{ maxWidth: 100, padding: 0 }}>
                  {text}
                </Text>
              </Tooltip>
            </div>
          ),
          searchBy,
          searchText
        ),
      ...onClickTable()
    },
    withSorting(tableColumns.tmDisplayName, {
      dataIndex: tableColumns.tmDisplayName.key,
      width: 100,
      key: tableColumns.tmDisplayName.key,
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: getColumnSortOrder(sorting, "tmDisplayName"),
      render: (val, record) => renderWithLink(record, () => val, searchBy, searchText),
      ...onClickTable()
    }),
    withSorting(tableColumns.doseFormCategory, {
      dataIndex: tableColumns.doseFormCategory.key,
      width: 100,
      key: tableColumns.doseFormCategory.key,
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: getColumnSortOrder(sorting, "doseFormCategory"),
      render: (val, record) => renderWithLink(record, () => val, searchBy, searchText),
      ...onClickTable()
    }),
    {
      ...tableColumns.dosageDisplay,
      title: tableColumns.dosageDisplay.title,
      dataIndex: tableColumns.dosageDisplay.key,
      key: tableColumns.dosageDisplay.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(
          record,
          () => <div>{record.dosageDisplay ? record.dosageDisplay : null}</div>,
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "dosageDisplay"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    },
    {
      ...tableColumns.unitCost,
      dataIndex: tableColumns.unitCost.key,
      key: tableColumns.unitCost.key,
      width: 120,
      render: (text, record) => {
        const unitCost = record.currentOrder?.unitCost;
        return renderWithLink(
          record,
          () => (
            <Text style={{ fontSize: "15px", fontWeight: fontWeights.regular }}>
              {unitCost != null ? `$${Number(unitCost).toFixed(2)}` : ""}
            </Text>
          ),
          searchBy,
          searchText
        );
      },
      ...onClickTable()
    },
    {
      ...tableColumns.totalAmount,
      dataIndex: tableColumns.totalAmount.key,
      key: tableColumns.totalAmount.key,
      width: 150,
      render: (text, record) => {
        const totalAmount = record.currentOrder?.totalAmount;
        return renderWithLink(
          record,
          () => (
            <Text style={{ fontSize: "15px", fontWeight: fontWeights.regular }}>
              {totalAmount != null ? `$${Number(totalAmount).toFixed(2)}` : ""}
            </Text>
          ),
          searchBy,
          searchText
        );
      },
      ...onClickTable()
    },
    withSorting(tableColumns.assistance_program_name, {
      dataIndex: tableColumns.assistance_program_name.key,
      key: tableColumns.assistance_program_name.key,
      width: 150,
      render: (text, record) =>
        renderWithLink(
          record,
          () => <div>{record.potentialSaving ? record.potentialSaving.assistanceProgramName : null}</div>,
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "assistance_program_name"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.drug_manufacturer, {
      dataIndex: tableColumns.drug_manufacturer.key,
      key: tableColumns.drug_manufacturer.key,
      width: 150,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div style={{ minWidth: "150px" }}>{record.potentialSaving ? record.potentialSaving.providedBy : null}</div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "drug_manufacturer"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.facility, {
      dataIndex: tableColumns.facility.key,
      key: tableColumns.facility.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(record, () => <div>{record.facilityName || null}</div>, searchBy, searchText),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      sortOrder: getColumnSortOrder(sorting, "facility"),
      ...onClickTable()
    }),
    withSorting(tableColumns.physician, {
      dataIndex: tableColumns.physician.key,
      key: tableColumns.physician.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(record, () => <div>{record.physician?.name || null}</div>, searchBy, searchText),
      sortOrder: getColumnSortOrder(sorting, "physician"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.order_date, {
      dataIndex: tableColumns.order_date.key,
      key: tableColumns.order_date.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div style={{ minWidth: "70px" }}>
              {record.currentOrder.orderDate
                ? moment(record.currentOrder.orderDate).format(DATE_FORMAT_2)
                : INVALID_DATE}
            </div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "order_date"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.admin_date, {
      dataIndex: tableColumns.admin_date.key,
      key: tableColumns.admin_date.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div>
              {record.currentOrder.administrationDate
                ? moment(record.currentOrder.administrationDate).format(DATE_FORMAT_2)
                : INVALID_DATE}
            </div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "admin_date"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.last_admin, {
      dataIndex: tableColumns.last_admin.key,
      width: 100,
      key: tableColumns.last_admin.key,

      render: (text, record) =>
        renderWithLink(
          record,
          () => <div>{record.lastAdminDate && moment(record.lastAdminDate).format(DATE_FORMAT_2)}</div>,
          searchBy,
          searchText
        ),
      ...onClickTable()
    }),
    withSorting(tableColumns.estimated_delivery, {
      width: 100,
      dataIndex: tableColumns.estimated_delivery.key,
      key: tableColumns.estimated_delivery.key,

      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div>
              {record.currentOrder.estimatedDelivery
                ? moment(record.currentOrder.estimatedDelivery).format(DATE_FORMAT_2)
                : null}
            </div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "estimated_delivery"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    withSorting(tableColumns.arrive_date, {
      dataIndex: tableColumns.arrive_date.key,
      key: tableColumns.arrive_date.key,
      width: 100,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div>
              {record.currentOrder.arriveDate ? moment(record.currentOrder.arriveDate).format(DATE_FORMAT_2) : null}
            </div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "arrive_date"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    }),
    {
      ...tableColumns.order_type,
      width: 100,
      title: () => {
        return <div>{tableColumns.order_type.title}</div>;
      },
      dataIndex: tableColumns.order_type.key,
      key: tableColumns.order_type.key,
      render: (text, record) =>
        renderWithLink(
          record,
          () => (
            <div
              style={{
                display: "flex",
                minWidth: "70px",
                justifyContent: "start"
              }}
            >
              <Tooltip
                title={
                  <Text>
                    {record.currentOrder.isFreeDrug
                      ? t("orders_table.order_type_free")
                      : t("orders_table.order_type_replacement")}
                  </Text>
                }
                color={WHITE}
              >
                {record.currentOrder.isFreeDrug ? <FreeIcon /> : <ReplacementIcon />}
              </Tooltip>
            </div>
          ),
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "order_type"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      ...onClickTable()
    },
    withSorting(tableColumns.cycle_number, {
      dataIndex: tableColumns.cycle_number.key,
      key: tableColumns.cycle_number.key,
      render: (text, record) =>
        renderWithLink(
          record,
          () => {
            const isCurrent = record.currentOrder.id === record.pharmacyPatientCurrentOrder;
            return (
              <Text style={{ fontSize: "15px", fontWeight: fontWeights.regular }}>{`${
                record.currentOrder ? record.currentOrder.orderIndex + 1 : ""
              } ${isCurrent ? t("form.cycle.current_indicator") : ""}`}</Text>
            );
          },
          searchBy,
          searchText
        ),
      sortOrder: getColumnSortOrder(sorting, "cycle_number"),
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      width: 120,
      ...onClickTable()
    }),
    {
      ...tableColumns.status,
      title: () => {
        return <div>{tableColumns.status.title}</div>;
      },
      dataIndex: tableColumns.status.key,
      key: tableColumns.status.key,
      width: 150,
      render: (_text, record) =>
        renderWithLink(
          record,
          () => <StatusIcon status={record.currentOrder.status} isFreeDrug={record.currentOrder.isFreeDrug} />,
          searchBy,
          searchText
        ),
      ...onClickTable()
    },
    {
      ...tableColumns.action,
      title: () => {
        return <div>{tableColumns.action.title}</div>;
      },
      width: 200,
      align: "left",
      dataIndex: tableColumns.action.key,
      key: tableColumns.action.key,
      render: (text, record) => <ActionButton patient={record} actionButtonId="cycle_action_btn_list" />,
      onHeaderCell
    },
    {
      ...tableColumns.notes,
      dataIndex: tableColumns.notes.key,
      key: tableColumns.notes.key,
      render: () => (
        <Tooltip title={<Text>{t("add_note")}</Text>} color={"white"}>
          <div id="list_add_note" data-tm-id="add-note-btn" style={{ display: "flex", maxWidth: "50px" }}>
            <NoteIcon />
          </div>
        </Tooltip>
      ),
      width: 50,
      ...onClickTable(3)
    },
    {
      ...tableColumns.attachments,
      dataIndex: tableColumns.attachments.key,
      key: tableColumns.attachments.key,
      render: () => (
        <Tooltip title={<Text>{t("add_attachment")}</Text>} color={"white"}>
          <div id="list_add_attachment" data-tm-id="add-attachement-btn" style={{ display: "flex", maxWidth: "50px" }}>
            <AttachIcon />
          </div>
        </Tooltip>
      ),
      width: 100,
      ...onClickTable(4)
    }
  ];

  const onTableChange = (pagination, filters, sorter) => {
    let { current: nextPage } = pagination;

    if (nextPage === currentPage) {
      const { columnIschanged, sortDirectionIschanged, nextColumnName, nextSortBy } = getSortingChanges(
        sorting,
        sorter
      );
      setSortingAct(nextSortBy ? { columnKey: nextColumnName, order: nextSortBy } : defaultSort);
      if ((columnIschanged || sortDirectionIschanged) && currentPage !== 1) {
        isSearch ? setSearchResultCurrentPageAct(1) : setCurrentPageAct(1);
        nextPage = 1;
      }
    } else {
      isSearch ? setSearchResultCurrentPageAct(nextPage) : setCurrentPageAct(nextPage);
    }
    setIsLoadingEnabledAct(true);
    fetchOrders(nextPage, ApiNames.SearchWithoutRefresh, sorter);
    scrollTableToTop(tableId);
  };

  return (
    <div id="list-view">
      <>
        <StyledTable
          id={tableId}
          loading={orderTableLoading || isFetching}
          onChange={onTableChange}
          columns={columns.filter((col) => tableColumns[col.dataIndex].display)}
          locale={{ emptyText: <NoOrdersFoundIcon /> }}
          isSavedListView={isSavedListView}
          filtersVisible={filtersVisible}
          isSearch={isSearch}
          pagination={{
            style: { margin: 0 },
            size: "small",
            current: currentPage,
            showSizeChanger: false,
            defaultPageSize: PAGE_SIZE,
            pageSize: PAGE_SIZE,
            position: ["bottom"],
            onChange: (page) => (isSearch ? setSearchResultCurrentPageAct(1) : setCurrentPageAct(page)),
            total: total,
            showTotal: (total, range) => (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Text>
                  {t("savedList.saved_list_orders_page_count", {
                    range_start: range[0],
                    range_end: range[1] ? range[1] : range[0],
                    total
                  })}
                </Text>
              </div>
            )
          }}
          dataSource={patients.map((patient, index) => {
            const { assignees, rxnormDrug } = patient;
            return {
              ...patient,
              key: index,
              assigneeNames: formatAssigneesToDisplay(assignees),
              tmDisplayName: rxnormDrug?.tmDisplayName,
              doseFormCategory: getDoseFormCategory(rxnormDrug)
            };
          })}
        />
      </>
      <FilterListForm setFormPopupsAct={setFormPopupsAct} />
    </div>
  );
}

const mapStateToProps = createStructuredSelector({
  tableColumns: selectPatientTableColumns,
  site: selectSite
});

const OrdersTable = connect(mapStateToProps)(withTranslation()(OrdersTableComponent));

export { OrdersTable };
